import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../constants';
import { useAppDispatch } from '../../hooks';
import { setAnalyticsFilters, setReviewFilters } from '../../redux/reducers';
import { AnalyticsFilterKeys, FilterKeys, ReviewFilterKeys, SimulationsFilterKeys } from '../../types';
import { getAnalyticsActiveTab, getCurrentAppRoute } from '../../utils';
import { ConfirmModal } from '../modals';

interface ClearFiltersDialogProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const ClearFiltersDialog = ({ isOpen, setIsOpen }: ClearFiltersDialogProps) => {
  const dispatch = useAppDispatch();

  const location = useLocation();
  const navigate = useNavigate();

  const currRoute = getCurrentAppRoute();

  // Memoize the URLSearchParams object to avoid unnecessary recalculations.
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // Clears the filter parameters in the URL, given the filter keys.
  const clearParams = useCallback((keys: FilterKeys[]) => keys.forEach((key) => params.delete(key)), [params]);

  // Clears the filter parameters in the URL, given the current page.
  // We clear the Redux filters as well to ensure that they do not re-populate the URL after clearing the filters,
  // since the filters are saved in the Redux store.
  const clearFilterParams = useCallback(() => {
    switch (currRoute) {
      case AppRoutes.SIMULATIONS:
        clearParams(Object.values(SimulationsFilterKeys));
        break;
      case AppRoutes.REVIEW:
        dispatch(setReviewFilters({}));
        clearParams(Object.values(ReviewFilterKeys));
        break;
      case AppRoutes.ANALYTICS: {
        // We do not handle default filters here because it is already handled by the route loader.
        const activeTab = getAnalyticsActiveTab();
        dispatch(setAnalyticsFilters({ tab: activeTab, ...{} }));
        clearParams(Object.values(AnalyticsFilterKeys));
        break;
      }
    }

    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [currRoute, params, navigate, location.pathname, clearParams, dispatch]);

  // Clears the filter parameters in the URL and closes the dialog.
  const onConfirm = () => {
    clearFilterParams();
    setIsOpen(false);
  };

  return (
    <ConfirmModal
      buttonText="Clear filters"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={onConfirm}
      title="Confirm clear filters"
      confirmText="Are you sure you want to clear all filters?"
    />
  );
};

export default ClearFiltersDialog;
